import React from 'react'
import mapboxgl from 'mapbox-gl'
import { UnifiedRoute } from '../../../services/firebase/routes'
import { createMarkerElement } from '../../../utils/routeMarkerHelpers'
import { createRouteHoverPanel } from '../RouteHoverPanel'
import { UnifiedRouteRenderer } from '../../../utils/unifiedRouteRenderer'
import {
  getRouteCoordinates,
  getTotalDistance,
  getTotalElevationGain,
  calculateUnpavedPercentage,
  getRouteTypeDisplay,
  formatRouteLocation,
  getRouteRegion
} from '../../../utils/routeDisplay'

interface CreateRouteMarkerParams {
  route: UnifiedRoute
  map: mapboxgl.Map
  unifiedRenderer: UnifiedRouteRenderer | null
  onRouteSelect: (route: any) => void
  selectedRoute: any | null
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>
  alwaysRenderRoutes: boolean
  onLazyLoadRoute?: (routeId: string) => Promise<any>
  activeHoverRef: React.MutableRefObject<string | null>
  activeSelectionRef: React.MutableRefObject<string | null>
  setHoveredRouteId: (id: string | null) => void
  onPhotoLoad?: (routeId: string) => Promise<string[]>
  embedMode: boolean
}

export function createSingleRouteMarker({
  route,
  map,
  unifiedRenderer,
  onRouteSelect,
  selectedRoute,
  markersRef,
  alwaysRenderRoutes,
  onLazyLoadRoute,
  activeHoverRef,
  activeSelectionRef,
  setHoveredRouteId,
  onPhotoLoad,
  embedMode
}: CreateRouteMarkerParams) {
  console.log('🎯 createSingleRouteMarker called for route:', route.id, 'alwaysRenderRoutes:', alwaysRenderRoutes)
  
  // Get coordinates
  const coords = getRouteCoordinates(route)
  if (!coords) {
    console.warn('No coordinates found for route:', route.id)
    return
  }
  const [lng, lat] = coords

  const supportsHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  const enableHoverInteractions = supportsHover

  // Create marker element
  const markerElement = createMarkerElement(route.id, route.routeType || 'single')

  // Hover state
  let isHovered = false
  let hoverPanelContainer: HTMLDivElement | null = null
  let hoverPanelController: any = null
  let hoverTimeout: NodeJS.Timeout | null = null
  let isMouseOverPanel = false
  let isMouseOverMarker = false
  let isRenderingRoute = false // Lock to prevent clearing during render

  // Update marker visual style
  const updateMarkerStyle = () => {
    const pin = markerElement.querySelector('.marker-pin') as HTMLElement
    const container = markerElement.querySelector('.marker-container') as HTMLElement
    
    if (pin && container) {
      const isSelected = selectedRoute?.firebaseRoute?.id === route.id || 
                        selectedRoute?.id === route.id
      
      if (isHovered || isSelected) {
        pin.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.2) 100%)'
        pin.style.backdropFilter = 'blur(2px)'
        pin.style.webkitBackdropFilter = 'blur(2px)'
        pin.style.border = '1.5px solid rgba(16,185,129,0.15)'
        pin.style.transform = 'rotate(-45deg) scale(1.1)'
        pin.style.boxShadow = '0 6px 20px rgba(16,185,129,0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
        container.style.transform = 'scale(1.1)'
      } else {
        pin.style.background = 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(31,41,55,0.35) 100%)'
        pin.style.backdropFilter = 'blur(1px)'
        pin.style.webkitBackdropFilter = 'blur(1px)'
        pin.style.border = '1.5px solid rgba(255,255,255,0.04)'
        pin.style.transform = 'rotate(-45deg) scale(1)'
        pin.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.025)'
        container.style.transform = 'scale(1)'
      }
    }
  }

  const collectHighlightImages = (routeData: any): string[] => {
    if (!routeData?.photos || routeData.photos.length === 0) {
      return []
    }

    const images: string[] = []

    if (routeData.highlightImages?.main) {
      const mainPhoto = routeData.photos.find((p: any) => p.id === routeData.highlightImages!.main)
      if (mainPhoto?.url) {
        images.push(mainPhoto.url)
      }
    }

    if (routeData.highlightImages?.additional) {
      routeData.highlightImages.additional.forEach((photoId: string) => {
        const photo = routeData.photos?.find((p: any) => p.id === photoId)
        if (photo?.url) {
          images.push(photo.url)
        }
      })
    }

    if (images.length === 0) {
      const photosWithUrls = routeData.photos.filter((p: any) => p.url)
      photosWithUrls.slice(0, 5).forEach((photo: any) => {
        images.push(photo.url)
      })
    }

    return images.filter((url: string | undefined) =>
      Boolean(url) &&
      !url.includes('staticmap') &&
      !url.includes('static-map') &&
      !url.includes('maps.googleapis.com') &&
      !url.includes('mapbox.com')
    )
  }

  // Create hover panel
  const createHoverPanel = () => {
    if (!enableHoverInteractions) {
      return
    }
    // Clean up any existing hover panels globally first
    const existingPanels = document.querySelectorAll('.route-hover-panel-container')
    existingPanels.forEach(panel => panel.remove())
    
    if (hoverPanelContainer) {
      hoverPanelContainer.remove()
    }
    
    hoverPanelContainer = document.createElement('div')
    hoverPanelContainer.className = 'route-hover-panel-container'
    hoverPanelContainer.style.pointerEvents = 'none' // Initially disable pointer events
    document.body.appendChild(hoverPanelContainer)
    
    const rect = markerElement.getBoundingClientRect()
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top - 10 // Position above marker
    }
    
    hoverPanelController = createRouteHoverPanel(
      hoverPanelContainer,
      route,
      position,
      embedMode ? onPhotoLoad : undefined
    )
    
    
    hoverPanelController.setOnMouseEnter(() => {
      isMouseOverPanel = true
      if (hoverTimeout) clearTimeout(hoverTimeout)
    })
    
    hoverPanelController.setOnMouseLeave(() => {
      isMouseOverPanel = false
      if (hoverTimeout) clearTimeout(hoverTimeout)
      hoverTimeout = setTimeout(() => {
        if (!isMouseOverMarker) {
          hoverPanelController?.destroy()
          if (hoverPanelContainer) {
            hoverPanelContainer.remove()
            hoverPanelContainer = null
            hoverPanelController = null
          }
          isHovered = false
          updateMarkerStyle()
        }
      }, 200)
    })
    
    // Enable pointer events after a delay to prevent interference
    setTimeout(() => {
      if (hoverPanelContainer && isMouseOverMarker) {
        hoverPanelContainer.style.pointerEvents = 'auto'
      }
    }, 100)
    
    hoverPanelController.setOnClick(() => {
      // Clear hover state to prevent race conditions
      isHovered = false
      isMouseOverMarker = false
      isMouseOverPanel = false
      
      // Clean up ALL hover panels globally
      const allHoverPanels = document.querySelectorAll('.route-hover-panel-container')
      allHoverPanels.forEach(panel => panel.remove())
      
      // Close this hover panel
      if (hoverPanelController) {
        hoverPanelController.destroy()
        hoverPanelController = null
      }
      if (hoverPanelContainer) {
        hoverPanelContainer.remove()
        hoverPanelContainer = null
      }
      
      // Clear hover state in parent component
      if (activeHoverRef.current === route.id) {
        activeHoverRef.current = null
        setHoveredRouteId(null)
      }
      
      // Show RoutePopupCard without panning
      onRouteSelect({
        id: route.id,
        name: route.name,
        routeType: getRouteTypeDisplay(route.routeType),
        coordinates: route.coordinates || [],
        location: formatRouteLocation(route),
        region: getRouteRegion(route),
        distance: getTotalDistance(route),
        elevationGain: getTotalElevationGain(route),
        surface: `${100 - calculateUnpavedPercentage(route)}% paved`,
        unpavedPercentage: calculateUnpavedPercentage(route),
        description: route.description || '',
        creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
        creatorPhoto: route.ownerPhoto || null,
        organization: route.submission?.clubName || 'Personal Route',
        clubLogo: (route as any).clubLogo || null,
        attribution: route.attribution,
        images: (() => {
          const images: string[] = []
          // Add main highlight image first
          if (route.highlightImages?.main && route.photos) {
            const mainPhoto = route.photos.find(p => p.id === route.highlightImages!.main)
            if (mainPhoto?.url) images.push(mainPhoto.url)
          }
          // Add additional highlight images
          if (route.highlightImages?.additional && route.photos) {
            route.highlightImages.additional.forEach(photoId => {
              const photo = route.photos?.find(p => p.id === photoId)
              if (photo?.url) images.push(photo.url)
            })
          }
          // If no highlight images, fallback to first few photos
          if (images.length === 0 && route.photos && route.photos.length > 0) {
            images.push(...route.photos.slice(0, 5).map(p => p.url))
          }
          return images
        })(),
        thumbnail: route.thumbnail || null,
        firebaseRoute: route
      })
      
      // Render the route on the map
      if (!alwaysRenderRoutes && unifiedRenderer) {
        if (route.routes?.[0]?.coordinates?.length > 1 || route.coordinates?.length > 1) {
          unifiedRenderer.renderSelectedRoute(route)
        }
      }
    })
  }

  // Mouse enter handler
  markerElement.addEventListener('mouseenter', async () => {
    if (!enableHoverInteractions) {
      return
    }
    console.log(`🖱️ Mouse enter ${route.id} - isMouseOverMarker: ${isMouseOverMarker}`)
    isHovered = true
    isMouseOverMarker = true
    updateMarkerStyle()
    
    if (hoverTimeout) clearTimeout(hoverTimeout)
    
    // Render route on hover if not always rendering
    if (!alwaysRenderRoutes && unifiedRenderer) {
      activeHoverRef.current = route.id
      setHoveredRouteId(route.id)
      
      // Use async function to handle rendering
      const renderRoute = async () => {
        isRenderingRoute = true // Set lock
        
        // Check if route needs to be lazy loaded
        let routeToRender = route
        const hasFullData = (route.coordinates && route.coordinates.length > 1) || 
                           (route.routes && route.routes[0]?.coordinates?.length > 1)
        
        if (!hasFullData && onLazyLoadRoute) {
          console.log(`🔄 Lazy loading route ${route.name} for hover`)
          const fullRoute = await onLazyLoadRoute(route.id)
          if (fullRoute && activeHoverRef.current === route.id) {
            routeToRender = fullRoute
          }
        }
        
        // Render the route on the map
        if (activeHoverRef.current === route.id) {
          console.log(`🗺️ Rendering route ${routeToRender.name} on hover`)
          unifiedRenderer.renderHoveredRoute(routeToRender)
        }
        
        // Release lock after a longer delay to ensure render is complete
        // This prevents the route from being cleared while still rendering
        setTimeout(() => {
          isRenderingRoute = false
        }, 500)
      }
      
      renderRoute()
    }
    
    // Create hover panel after starting route render to avoid interference
    if (!hoverPanelController) {
      console.log(`📋 Creating hover panel for ${route.id}`)
      // Small delay to ensure route render has started
      setTimeout(() => {
        if (isMouseOverMarker && !hoverPanelController) {
          createHoverPanel()
        }
      }, 50)
    }
  })

  // Mouse leave handler
  markerElement.addEventListener('mouseleave', () => {
    if (!enableHoverInteractions) {
      return
    }
    console.log(`👋 Mouse leave ${route.id}`)
    isMouseOverMarker = false
    
    if (hoverTimeout) clearTimeout(hoverTimeout)
    hoverTimeout = setTimeout(() => {
      if (!isMouseOverPanel && hoverPanelController) {
        hoverPanelController.destroy()
        if (hoverPanelContainer) {
          hoverPanelContainer.remove()
          hoverPanelContainer = null
          hoverPanelController = null
        }
        isHovered = false
        updateMarkerStyle()
        
        // Clear route hover if not always rendering and not hovering panel
        if (!alwaysRenderRoutes && unifiedRenderer && !isMouseOverPanel) {
          // Check if still rendering - if so, wait before clearing
          if (isRenderingRoute) {
            console.log(`⏳ Route ${route.id} still rendering, delaying clear`)
            setTimeout(() => {
              if (!isMouseOverPanel && !isMouseOverMarker && activeHoverRef.current === route.id) {
                console.log(`🧹 Clearing route ${route.id} after render delay`)
                activeHoverRef.current = null
                setHoveredRouteId(null)
                unifiedRenderer.clearHoveredRoute()
              }
            }, 300)
          } else if (activeHoverRef.current === route.id) {
            console.log(`🧹 Clearing route ${route.id} - mouse not over marker or panel`)
            activeHoverRef.current = null
            setHoveredRouteId(null)
            unifiedRenderer.clearHoveredRoute()
          }
        }
      }
    }, 200)
  })

  // Click handler
  markerElement.addEventListener('click', () => {
    if (hoverTimeout) clearTimeout(hoverTimeout)
    
    // Clear hover state to prevent race conditions
    isHovered = false
    isMouseOverMarker = false
    
    // Clean up ALL hover panels globally
    const allHoverPanels = document.querySelectorAll('.route-hover-panel-container')
    allHoverPanels.forEach(panel => panel.remove())
    
    // Clean up this marker's hover panel references
    if (hoverPanelController) {
      hoverPanelController.destroy()
      hoverPanelController = null
    }
    if (hoverPanelContainer) {
      hoverPanelContainer.remove()
      hoverPanelContainer = null
    }
    
    // Clear hover state in parent component
    if (activeHoverRef.current === route.id) {
      activeHoverRef.current = null
      setHoveredRouteId(null)
    }
    
    const baseImages = collectHighlightImages(route)

    const baseCard = {
      id: route.id,
      name: route.name,
      routeType: getRouteTypeDisplay(route.routeType),
      coordinates: route.coordinates || [],
      location: formatRouteLocation(route),
      region: getRouteRegion(route),
      distance: getTotalDistance(route),
      elevationGain: getTotalElevationGain(route),
      surface: `${100 - calculateUnpavedPercentage(route)}% paved`,
      unpavedPercentage: calculateUnpavedPercentage(route),
      description: route.description || '',
      creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
      creatorPhoto: route.ownerPhoto || null,
      organization: route.submission?.clubName || 'Personal Route',
      clubLogo: (route as any).clubLogo || null,
      attribution: route.attribution,
      images: baseImages,
      thumbnail: route.thumbnail || null,
      firebaseRoute: route
    }

    activeSelectionRef.current = route.id
    onRouteSelect(baseCard)

    if (onLazyLoadRoute) {
      console.log('📸 Marker click - fetching full route data for popup asynchronously', route.id)
      onLazyLoadRoute(route.id)
        .then((loadedRoute) => {
          if (!loadedRoute) {
            return
          }
          if (activeSelectionRef.current !== route.id) {
            return
          }

          const enhancedImages = collectHighlightImages(loadedRoute)
          onRouteSelect({
            ...baseCard,
            coordinates: loadedRoute.coordinates || baseCard.coordinates,
            description: loadedRoute.description || baseCard.description,
            images: enhancedImages.length > 0 ? enhancedImages : baseCard.images,
            firebaseRoute: loadedRoute
          })
        })
        .catch((error) => {
          console.warn('Failed to load full route data for marker popup:', error)
        })
    }
  })

  // Apply initial style
  updateMarkerStyle()

  // Create the marker
  console.log('📌 Creating new mapboxgl.Marker for route:', route.id)
  
  // Safety check - ensure map still exists and has a canvas container
  if (!map || typeof map.getCanvasContainer !== 'function') {
    console.warn('⚠️ Map instance is invalid, skipping marker creation for route:', route.id)
    return
  }
  
  try {
    const marker = new mapboxgl.Marker({
      element: markerElement
    })
      .setLngLat([lng, lat])
      .addTo(map)

    // Clean up on removal
    ;(marker as any).remove = (() => {
      const originalRemove = marker.remove.bind(marker)
      return () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)
        if (hoverPanelController) {
          hoverPanelController.destroy()
        }
        if (hoverPanelContainer) {
          hoverPanelContainer.remove()
          hoverPanelContainer = null
        }
        originalRemove()
      }
    })()

    markersRef.current.push(marker)
  } catch (error) {
    console.error('❌ Failed to create marker for route:', route.id, error)
    // Clean up the marker element if marker creation failed
    markerElement.remove()
  }
}
